import {
  StartGameResponse,
  ScoreResult,
  LeaderboardResponse,
  LeaderboardEntry,
  UserDecision,
} from '../types/index.js';

export interface SaveScoreRequest {
  sessionId: string;
  playerName: string;
  playerEmail: string;
  score: number;
  fraudPrevented: number;
}

const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://127.0.0.1:3000';
const API_KEY = import.meta.env.VITE_API_KEY ?? '';

const authHeaders = (): Record<string, string> =>
  API_KEY ? { 'X-Api-Key': API_KEY } : {};

async function post<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Request failed');
  }
  return res.json();
}

async function get<T>(path: string, params?: Record<string, string>): Promise<T> {
  return getWithHeaders(path, {}, params);
}

async function getWithHeaders<T>(path: string, extraHeaders: Record<string, string>, params?: Record<string, string>): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => url.searchParams.set(k, v));
  }
  const res = await fetch(url.toString(), { headers: { ...authHeaders(), ...extraHeaders } });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    throw new Error(err.error ?? 'Request failed');
  }
  return res.json();
}

export const api = {
  startGame(storylineId?: string): Promise<StartGameResponse> {
    return post('/game/start', storylineId ? { storylineId } : {});
  },

  submitDecisions(
    sessionId: string,
    decisions: UserDecision[],
  ): Promise<ScoreResult> {
    return post('/game/submit', { sessionId, decisions });
  },

  saveScore(req: SaveScoreRequest): Promise<void> {
    return post('/game/save-score', req);
  },

  getLeaderboard(date?: string): Promise<LeaderboardResponse> {
    return get('/leaderboard', date ? { date } : undefined);
  },

  adminVerify(secret: string): Promise<{ ok: boolean }> {
    return post('/admin/verify', { secret });
  },

  adminLeaderboard(secret: string, date?: string): Promise<LeaderboardResponse> {
    return getWithHeaders('/admin/leaderboard', { 'X-Admin-Secret': secret }, date ? { date } : undefined);
  },

  adminExport(secret: string): Promise<{ entries: LeaderboardEntry[] }> {
    return getWithHeaders('/admin/export', { 'X-Admin-Secret': secret });
  },
};
